import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose } from 'react-icons/md';
import { FaPepperHot, FaMinus, FaPlus } from 'react-icons/fa';
import { formatPrice } from '../utils/priceUtils';
import { getSpiceIconCount, formatModifiersForDisplayWithQuantity } from '../utils/cartUtils';

interface DeleteVariantModalProps {
  isOpen: boolean;
  itemName: string;
  basePrice: number;
  variants: Array<{
    cartItemId: string;
    quantity: number;
    selected: {
      spice: any | null;
      modifiers: any[];
    };
    totalPrice: number;
  }>;
  onUpdateQuantity: (cartItemId: string, newQuantity: number) => void;
  onClose: () => void;
}

export const DeleteVariantModal: React.FC<DeleteVariantModalProps> = ({
  isOpen,
  itemName,
  basePrice,
  variants,
  onUpdateQuantity,
  onClose,
}) => {
  // Auto-close modal when all variants are removed
  useEffect(() => {
    if (isOpen && variants.length === 0) {
      onClose();
    }
  }, [isOpen, variants.length, onClose]);

  // ESC key handling + body scroll lock for accessibility
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Allow quantity to reach 0 (remove minimum restriction)
  const handleDecreaseQuantity = useCallback((
    cartItemId: string,
    currentQuantity: number
  ) => {
    if (currentQuantity < 0) return;
    onUpdateQuantity(cartItemId, currentQuantity - 1);
  }, [onUpdateQuantity]);

  // Memoized overlay click handler
  const handleOverlayClick = useCallback(() => onClose(), [onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100000] p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOverlayClick}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-variant-title"
            className="bg-white rounded-2xl shadow-xl w-full max-w-[95vw] sm:max-w-md md:max-w-lg max-h-[90dvh] flex flex-col relative"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 sm:p-2.5 hover:bg-gray-100 rounded-full transition-colors z-10"
              aria-label="Close"
            >
              <MdClose className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
            </button>

            {/* Header - Fixed */}
            <div className="p-4 sm:p-5 md:p-6 pb-3 sm:pb-4 border-b border-gray-200">
              <h2 id="delete-variant-title" className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2 pr-8">
                Choose Item to Remove
              </h2>

              <p className="text-xs sm:text-sm md:text-base text-gray-600">
                This item has multiple modifiers. Choose the item you want to remove.
              </p>
            </div>

            {/* Scrollable Variants List */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-3 sm:py-4 space-y-3 sm:space-y-4">
              {variants.map((variant) => {
                const formattedModifiers = formatModifiersForDisplayWithQuantity(variant.selected.modifiers, 1);
                
                return (
                  <div
                    key={variant.cartItemId}
                    className="p-3 sm:p-4 rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all"
                  >
                    <div className="space-y-2 sm:space-y-3">
                      {/* Product Name and Base Price */}
                      <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                        <span className="font-semibold text-sm sm:text-base text-gray-900">{itemName}</span>
                        <span className="font-bold text-sm sm:text-base text-gray-900">{formatPrice(basePrice)}</span>
                      </div>

                      {/* Spice Level */}
                      {variant.selected?.spice && (
                        <div className="flex items-center gap-1">
                          <div className="flex">
                            {Array.from({ length: getSpiceIconCount(variant.selected.spice) }, (_, i) => (
                              <FaPepperHot 
                                key={i}
                                className={`w-3 h-3 sm:w-4 sm:h-4 text-red-500 ${i > 0 ? 'ml-0.5' : ''}`}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Modifiers - Show ALL prices including $0.00 */}
                      {formattedModifiers.length > 0 && (
                        <div>
                          <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                            {formattedModifiers.map((modifier: any, index: number) => (
                              <div key={index} className="flex items-center justify-between">
                                <span className="font-medium leading-snug">{modifier.options}</span>
                                <span className="text-green-600 text-xs sm:text-sm">({modifier.formattedPrice})</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Quantity Controls and Total Price */}
                      <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                        {/* Quantity Controls - Touch-safe */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDecreaseQuantity(variant.cartItemId, variant.quantity)}
                            className="w-9 h-9 sm:w-10 sm:h-10 rounded-md bg-gray-100 hover:bg-red-100 flex items-center justify-center transition-all"
                            aria-label="Decrease quantity"
                          >
                            <FaMinus className="w-3 h-3 sm:w-4 sm:h-4 text-gray-700" />
                          </button>
                          
                          <span className="text-sm sm:text-base font-semibold text-gray-900 min-w-[24px] text-center">
                            {variant.quantity}
                          </span>
                          
                          <button
                            disabled
                            className="w-9 h-9 sm:w-10 sm:h-10 rounded-md bg-gray-100 opacity-50 cursor-not-allowed flex items-center justify-center transition-all"
                            aria-label="Increase quantity (disabled)"
                          >
                            <FaPlus className="w-3 h-3 sm:w-4 sm:h-4 text-gray-700" />
                          </button>
                        </div>

                        {/* Total Price */}
                        <span className="text-sm sm:text-base md:text-lg font-bold text-gray-900">
                          {formatPrice(variant.totalPrice * variant.quantity)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DeleteVariantModal;
